import React, { useState, useEffect } from "react";
import { Box, Grid, TextField, Typography, Button, CircularProgress, MenuItem, Select, FormControl, InputLabel, Paper, Divider } from "@mui/material";
import { Save, Upload } from "lucide-react";
import { uploadAttachments } from "../../../api/index";
import { useTranslation } from "react-i18next";
const FONTS = [{
  value: "tajawal",
  label: "Tajawal (Arabic)"
}, {
  value: "cairo",
  label: "Cairo (Arabic)"
}, {
  value: "default",
  label: "System Default"
}, {
  value: "roboto",
  label: "Roboto"
}, {
  value: "noto-sans",
  label: "Noto Sans"
}];
const CDN_BASE = (process.env.REACT_APP_CDN_URL || process.env.REACT_APP_API || "http://localhost:5000").replace(/\/static\/uploads\/?$/, "").replace(/\/$/, "");
const toUrl = p => {
  if (!p) return "";
  if (p.startsWith("http")) return p;
  return `${CDN_BASE}/static/uploads/${p.replace(/^\/+/, "").replace(/^static\/uploads\/+/, "")}`;
};
const AppearanceTab = ({
  settings,
  save,
  saving
}) => {
  const {
    t
  } = useTranslation("website");
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState({});
  useEffect(() => {
    if (settings) setForm({
      primaryColor: settings.primaryColor || "#00838F",
      secondaryColor: settings.secondaryColor || "#EF6C00",
      fontFamily: settings.fontFamily || "tajawal",
      logoUrl: settings.logoUrl || "",
      faviconUrl: settings.faviconUrl || "",
      heroTitle: settings.heroTitle || "",
      heroSubtitle: settings.heroSubtitle || "",
      heroBgUrl: settings.heroBgUrl || "",
      aboutText: settings.aboutText || "",
      customCss: settings.customCss || ""
    });
  }, [settings]);
  const set = (key, val) => setForm(f => ({
    ...f,
    [key]: val
  }));
  const uploadImage = async (key, file) => {
    if (!file) return;
    setUploading(u => ({
      ...u,
      [key]: true
    }));
    try {
      const fd = new FormData();
      fd.append("files", file);
      fd.append("name", `website-${key}`);
      fd.append("description", `${key} image`);
      fd.append("subDirectory", "website");
      const res = await uploadAttachments(fd);
      const uploaded = res.data?.uploaded?.[0] || res.data?.data?.[0] || res.data?.[0];
      if (uploaded) {
        const path = `${(uploaded.subDirectory || "website").replace(/^\/+/, "").replace(/^static\/uploads\/+/, "")}/${uploaded.fileName}`;
        set(key, path);
      } else {
        throw new Error("Upload completed but no file metadata was returned");
      }
    } catch (e) {
      alert("Upload failed: " + (e.response?.data?.message || e.message));
    } finally {
      setUploading(u => ({
        ...u,
        [key]: false
      }));
    }
  };
  const ImageField = ({
    label,
    field
  }) => <Box>
      <Typography variant="body2" gutterBottom>
        {label}
      </Typography>
      {form[field] && <Box sx={{
      mb: 1
    }}>
          <img src={toUrl(form[field])} alt={label} style={{
        maxHeight: 80,
        maxWidth: "100%",
        borderRadius: 4,
        border: "1px solid #eee"
      }} />
      
        </Box>}
      <Button variant="outlined" size="small" component="label" startIcon={uploading[field] ? <CircularProgress size={14} /> : <Upload size={14} />} disabled={!!uploading[field]}>
      
        {uploading[field] ? t("Uploading…") : `Upload ${label}`}
        <input type="file" hidden accept="image/*" onChange={e => uploadImage(field, e.target.files[0])} />
      
      </Button>
    </Box>;
  return <Box>
      <Grid container spacing={3}>
        {/* Colors */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>{t("Brand Colors")}

          </Typography>
          <Box sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap"
        }}>
            <Box>
              <Typography variant="caption" display="block" gutterBottom>{t("Primary Color")}

              </Typography>
              <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 1
            }}>
                <input type="color" value={form.primaryColor || "#00838F"} onChange={e => set("primaryColor", e.target.value)} style={{
                width: 48,
                height: 36,
                border: "none",
                cursor: "pointer",
                borderRadius: 4
              }} />
                
                <TextField size="small" value={form.primaryColor || ""} onChange={e => set("primaryColor", e.target.value)} sx={{
                width: 110
              }} placeholder="#00838F" />
                
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" display="block" gutterBottom>{t("Secondary Color")}

              </Typography>
              <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 1
            }}>
                <input type="color" value={form.secondaryColor || "#EF6C00"} onChange={e => set("secondaryColor", e.target.value)} style={{
                width: 48,
                height: 36,
                border: "none",
                cursor: "pointer",
                borderRadius: 4
              }} />
                
                <TextField size="small" value={form.secondaryColor || ""} onChange={e => set("secondaryColor", e.target.value)} sx={{
                width: 110
              }} placeholder="#EF6C00" />
                
              </Box>
            </Box>
            <Paper variant="outlined" sx={{
            p: 1.5,
            display: "flex",
            gap: 1.5,
            alignItems: "center"
          }}>
              
              <Box sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: form.primaryColor || "#00838F"
            }} />
              
              <Box sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: form.secondaryColor || "#EF6C00"
            }} />
              
              <Typography variant="caption" color="text.secondary">{t("Preview")}

              </Typography>
            </Paper>
          </Box>
        </Grid>

        {/* Font */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>{t("Font Family")}</InputLabel>
            <Select value={form.fontFamily || "tajawal"} onChange={e => set("fontFamily", e.target.value)} label={t("Font Family")}>
              
              {FONTS.map(f => <MenuItem key={f.value} value={f.value}>
                  {f.label}
                </MenuItem>)}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Images */}
        <Grid item xs={12} sm={4}>
          <ImageField label={t("Logo")} field="logoUrl" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ImageField label={t("Favicon")} field="faviconUrl" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ImageField label={t("Hero Background")} field="heroBgUrl" />
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Hero Section */}
        <Grid item xs={12} sm={6}>
          <TextField label={t("Hero Title")} value={form.heroTitle || ""} onChange={e => set("heroTitle", e.target.value)} fullWidth placeholder={t("Welcome to Our Store")} />
          
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label={t("Hero Subtitle")} value={form.heroSubtitle || ""} onChange={e => set("heroSubtitle", e.target.value)} fullWidth placeholder={t("Quality products at great prices")} />
          
        </Grid>
        <Grid item xs={12}>
          <TextField label={t("About Us Text")} value={form.aboutText || ""} onChange={e => set("aboutText", e.target.value)} fullWidth multiline rows={4} placeholder={t("Tell your visitors about your business...")} />
          
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Custom CSS */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>{t("Custom CSS (Advanced)")}

          </Typography>
          <TextField value={form.customCss || ""} onChange={e => set("customCss", e.target.value)} fullWidth multiline rows={6} placeholder={"/* Add custom styles here */\n.hero { background: linear-gradient(...); }"} inputProps={{
          style: {
            fontFamily: "monospace",
            fontSize: 13
          }
        }} />
          
        </Grid>

        <Grid item xs={12}>
          <Button variant="contained" onClick={() => save(form)} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <Save size={16} />}>{t("Save Appearance")}


          </Button>
        </Grid>
      </Grid>
    </Box>;
};
export default AppearanceTab;
